import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { IMAGES } from "../../assets/Images/Image";
import { FiLock, FiEye, FiEyeOff, FiMail } from "react-icons/fi";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Dialog from "../components/Dialog";
import { WSAuthentication } from "../../services/remote/authentication/WSAuthentication";
import { validateEmail, validatePassword } from "../../helpers/Validator";

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const authInstance = WSAuthentication.getInstance();

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
    general: "",
  });
  const [email, setEmail] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: "", general: "" }));
  };

  const validateForm = () => {
    const generalError = validateEmail(email);
    setErrors((p) => ({ ...p, general: generalError }));

    if (generalError) return;
    const newErrors = {
      password: validatePassword(formData.password),
      confirmPassword:
        formData.password !== formData.confirmPassword
          ? t("login.passwords_do_not_match")
          : "",
      general: "",
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || !token) return;
    setIsLoading(true);
    try {
      await authInstance
        .resetPassword(email, formData.password, token)
        .then(() => {
          setShowSuccessDialog(true);
        });
    } catch (error: any) {
      setErrors((p) => ({
        ...p,
        general: error.message || t("reset_password_error"),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.langSwitcher}>
        <LanguageSwitcher />
      </div>

      <div style={styles.card}>
        <div style={styles.header}>
          <img src={IMAGES.LOGO} alt="Logo" style={styles.logo} />
          <h2 style={styles.title}>{t("login.reset_password")}</h2>
          <p style={styles.subtitle}>{t("login.enter_new_password")}</p>
        </div>

        {errors.general && <div style={styles.errorBox}>{errors.general}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          {/* Email */}
          <div style={styles.field}>
            <div
              style={{
                ...styles.inputWrapper,
                borderColor: errors.password ? "#ef4444" : "#e5e7eb",
              }}
            >
              <FiMail />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.email_placeholder")}
                style={styles.input}
              />
            </div>
          </div>
          {/* Password */}
          <div style={styles.field}>
            <div
              style={{
                ...styles.inputWrapper,
                borderColor: errors.password ? "#ef4444" : "#e5e7eb",
              }}
            >
              <FiLock />
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={t("login.new_password")}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.iconBtn}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.password && (
              <span style={styles.errorText}>{errors.password}</span>
            )}
          </div>

          {/* Confirm Password */}
          <div style={styles.field}>
            <div
              style={{
                ...styles.inputWrapper,
                borderColor: errors.confirmPassword ? "#ef4444" : "#e5e7eb",
              }}
            >
              <FiLock />
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t("login.confirm_password")}
                style={styles.input}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                style={styles.iconBtn}
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {errors.confirmPassword && (
              <span style={styles.errorText}>{errors.confirmPassword}</span>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            style={{ ...styles.submitBtn, opacity: isLoading ? 0.7 : 1 }}
          >
            {isLoading ? t("login.resetting") : t("login.reset_password")}
          </button>
        </form>

        <div style={styles.footer}>
          <p>
            {t("login.remember_password")}{" "}
            <a href="/login" style={styles.link}>
              {t("login.login_here")}
            </a>
          </p>
        </div>
      </div>

      <Dialog
        open={showSuccessDialog}
        onClose={() => navigate("/login")}
        title={t("success")}
        description={t("login.password_reset_success")}
        showYesButton={false}
        negativeButtonText={t("ok")}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #eef2ff, #f8fafc)",
    padding: 16,
    fontFamily: "Inter, system-ui, sans-serif",
  },
  langSwitcher: { position: "absolute", top: 16, right: 16 },
  card: {
    width: "100%",
    maxWidth: 420,
    background: "#ffffff",
    borderRadius: 16,
    padding: 32,
    boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
  },
  header: { textAlign: "center", marginBottom: 24 },
  logo: { height: 48, marginBottom: 12 },
  title: { fontSize: 22, fontWeight: 600, marginBottom: 4 },
  subtitle: { fontSize: 14, color: "#6b7280" },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "10px 12px",
    background: "#f9fafb",
  },
  input: {
    flex: 1,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 14,
  },
  iconBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    color: "#6b7280",
  },
  submitBtn: {
    marginTop: 8,
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "12px",
    fontSize: 15,
    fontWeight: 500,
    cursor: "pointer",
  },
  footer: {
    marginTop: 20,
    textAlign: "center",
    fontSize: 14,
    color: "#6b7280",
  },
  link: { color: "#4f46e5", textDecoration: "none", fontWeight: 500 },
  errorBox: {
    background: "#fee2e2",
    color: "#b91c1c",
    padding: 10,
    borderRadius: 10,
    fontSize: 13,
    marginBottom: 12,
  },
  errorText: { color: "#dc2626", fontSize: 12 },
};

export default ResetPassword;
